/**
 * Dashboard Create Page
 * Form to create a new dashboard
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, RefreshCw, LayoutDashboard } from 'lucide-react';
import { Layout } from '../components/layout';
import { dashboardsApi, endpointsApi } from '../api/client';

export function DashboardCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [selectedEndpoint, setSelectedEndpoint] = useState('');

  // Fetch available endpoints
  const { data: endpoints } = useQuery({
    queryKey: ['endpoints'],
    queryFn: endpointsApi.list,
  });

  const createMutation = useMutation({
    mutationFn: dashboardsApi.create,
    onSuccess: (dashboard) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      navigate(`/dashboards/${dashboard.id}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      auto_refresh: autoRefresh,
      refresh_interval_seconds: autoRefresh ? refreshInterval : undefined,
    });
  };

  return (
    <Layout title="New Dashboard">
      <div className="p-6 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/dashboards"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Create New Dashboard
            </h1>
            <p className="text-gray-500 mt-1">
              Configure your new visualization dashboard
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Dashboard Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Production Overview"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description for this dashboard"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent resize-none"
              />
            </div>

            {/* Default Endpoint */}
            <div>
              <label
                htmlFor="endpoint"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Default Data Source
              </label>
              <select
                id="endpoint"
                value={selectedEndpoint}
                onChange={(e) => setSelectedEndpoint(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent"
              >
                <option value="">Select an endpoint (optional)</option>
                {endpoints?.map((endpoint) => (
                  <option key={endpoint.id} value={endpoint.id}>
                    {endpoint.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Widgets will use this data source by default
              </p>
            </div>

            {/* Auto Refresh */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <label
                    htmlFor="autoRefresh"
                    className="text-sm font-medium text-gray-700"
                  >
                    Auto Refresh
                  </label>
                  <p className="text-xs text-gray-500">
                    Automatically refresh dashboard data
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="autoRefresh"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#45bdd6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#45bdd6]"></div>
                </label>
              </div>

              {autoRefresh && (
                <div className="mt-4">
                  <label
                    htmlFor="refreshInterval"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Refresh Interval (seconds)
                  </label>
                  <input
                    type="number"
                    id="refreshInterval"
                    value={refreshInterval}
                    onChange={(e) =>
                      setRefreshInterval(Math.max(10, parseInt(e.target.value) || 60))
                    }
                    min={10}
                    max={3600}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {createMutation.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : 'Failed to create dashboard'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Link
              to="/dashboards"
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={!name.trim() || createMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Create Dashboard
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
